/**
 * KPI Dashboard with Fixed Grid System
 * 
 * Architecture:
 * - Desktop: 100-column fixed grid (positions never change on resize)
 * - Mobile: 20-column fixed grid (positions never change on resize) 
 * - CSS scaling: Grid scales down/up on smaller screens but maintains exact positions
 * - Breakpoint switching: Auto-switches between desktop/mobile views at 768px
 * - View independence: Desktop and mobile layouts are completely separate
 * 
 * This prevents the layout repositioning issues that occurred with responsive grids.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, X, Activity, FileBarChart, DollarSign, Edit3, Eye, Trash2, Calendar, ChevronDown, Type, CheckCircle, AlertTriangle, ShoppingCart, Package2, Monitor, Smartphone, Settings } from 'lucide-react';
import PermissionGuard from '../components/PermissionGuard';
import { useBusiness } from '../contexts/BusinessContext';
import { 
  getKPIDashboards, 
  createKPIDashboard, 
  updateKPIDashboard,
  deleteKPIDashboard, 
  getDashboardReports,
  createDashboardReport,
  updateDashboardReportLayout,
  deleteDashboardReport,
  updateDashboardDefaultDateRange,
  configureOppositeView,
  markViewAsConfigured,
  detectDeviceType,
  KPIDashboard as DatabaseKPIDashboard,
  KPIDashboardReport as DatabaseKPIDashboardReport
} from '../lib/kpiDashboards';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import RGL, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { getSuppliers, Customer } from '../lib/customers';
import { createOrder, OrderItem as DBOrderItem, getPendingOrdersForItems } from '../lib/orders';
import { StockItem } from '../lib/stockItems';

const GridLayout = WidthProvider(RGL);

interface Dashboard {
  id: string;
  name: string;
  createdAt: Date;
  reports?: Report[];
  dateRange?: DateRangeOption;
  defaultDateRange?: DateRangeOption;
  createdDeviceType: 'desktop' | 'mobile';
  desktopConfigured: boolean;
  mobileConfigured: boolean;
}

interface Report {
  id: string;
  type: string;
  title: string;
  data?: any;
  layout: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
}

type DateRangeOption = 'this_month' | 'last_month' | 'next_month' | 'this_week' | 'last_week' | 'next_week';

interface DateRange {
  startDate: Date;
  endDate: Date;
}

// Interfaces for Stock Alert functionality
interface OrderItem {
  item: StockItem;
  quantityToOrder: number;
  totalCost: number;
}

interface SupplierOrder {
  supplier: Customer;
  items: OrderItem[];
  totalCost: number;
  itemCount: number;
}

// Utility functions for date range calculations
const getDateRange = (option: DateRangeOption): DateRange => {
  const now = new Date();
  const startOfWeek = (date: Date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day;
    return new Date(start.setDate(diff));
  };
  
  const endOfWeek = (date: Date) => {
    const end = new Date(startOfWeek(date));
    end.setDate(end.getDate() + 6);
    return end;
  };

  const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
  const endOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

  switch (option) {
    case 'this_month':
      return {
        startDate: startOfMonth(now),
        endDate: endOfMonth(now)
      };
    case 'last_month':
      return {
        startDate: startOfMonth(new Date(now.getFullYear(), now.getMonth() - 1)),
        endDate: endOfMonth(new Date(now.getFullYear(), now.getMonth() - 1))
      };
    case 'next_month':
      return {
        startDate: startOfMonth(new Date(now.getFullYear(), now.getMonth() + 1)),
        endDate: endOfMonth(new Date(now.getFullYear(), now.getMonth() + 1))
      };
    case 'this_week':
      return {
        startDate: startOfWeek(now),
        endDate: endOfWeek(now)
      };
    case 'last_week':
      const lastWeek = new Date(now);
      lastWeek.setDate(now.getDate() - 7);
      return {
        startDate: startOfWeek(lastWeek),
        endDate: endOfWeek(lastWeek)
      };
    case 'next_week':
      const nextWeek = new Date(now);
      nextWeek.setDate(now.getDate() + 7);
      return {
        startDate: startOfWeek(nextWeek),
        endDate: endOfWeek(nextWeek)
      };
    default:
      return {
        startDate: startOfMonth(now),
        endDate: endOfMonth(now)
      };
  }
};

const getDateRangeLabel = (option: DateRangeOption): string => {
  const labels: Record<DateRangeOption, string> = {
    'this_month': 'This Month',
    'last_month': 'Last Month', 
    'next_month': 'Next Month',
    'this_week': 'This Week',
    'last_week': 'Last Week',
    'next_week': 'Next Week'
  };
  return labels[option];
};

// Date Range Selector Component
interface DateRangeSelectorProps {
  value: DateRangeOption;
  onChange: (value: DateRangeOption) => void;
  defaultValue?: DateRangeOption;
  onSetDefault?: (value: DateRangeOption) => void;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ value, onChange, defaultValue, onSetDefault }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const options: DateRangeOption[] = [
    'this_month',
    'last_month', 
    'next_month',
    'this_week',
    'last_week',
    'next_week'
  ];

  const handleSelect = (option: DateRangeOption) => {
    onChange(option);
    setIsOpen(false);
  };

  const handleSetDefault = (option: DateRangeOption, event: React.MouseEvent) => {
    event.stopPropagation();
    if (onSetDefault) {
      onSetDefault(option);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isOpen && !target.closest('.date-range-selector')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative date-range-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
      >
        <Calendar className="h-4 w-4" />
        <span>{getDateRangeLabel(value)}</span>
        <ChevronDown className="h-4 w-4" />
      </button>
      
      {isOpen && (
        <div className="absolute top-full mt-1 left-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[200px]">
          <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-200">
            Select Date Range
          </div>
          {options.map((option) => (
            <div
              key={option}
              className={`flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 ${
                value === option ? 'bg-blue-50' : ''
              }`}
            >
              <button
                onClick={() => handleSelect(option)}
                className={`flex-1 text-left ${
                  value === option ? 'text-blue-700 font-medium' : 'text-gray-700'
                }`}
              >
                {getDateRangeLabel(option)}
              </button>
              {onSetDefault && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={defaultValue === option}
                    onChange={(e) => e.stopPropagation()}
                    onClick={(e) => handleSetDefault(option, e)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-500">Default</span>
                </div>
              )}
            </div>
          ))}
          {onSetDefault && (
            <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-200 bg-gray-50">
              Check a box to set as default for this dashboard
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function KPIDashboard() {
  const { businessId } = useBusiness();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dashboardName, setDashboardName] = useState('');
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddReportsModalOpen, setIsAddReportsModalOpen] = useState(false);
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [reportTitleTexts, setReportTitleTexts] = useState<{[key: string]: string}>({});
  
  // Stock Alert specific state
  const [suppliers, setSuppliers] = useState<Customer[]>([]);
  const [supplierOrders, setSupplierOrders] = useState<SupplierOrder[]>([]);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [pendingOrderItems, setPendingOrderItems] = useState<Map<string, number>>(new Map());
  
  // Device-specific view state
  const [currentViewType, setCurrentViewType] = useState<'desktop' | 'mobile'>(() => detectDeviceType());
  const [configuringView, setConfiguringView] = useState(false);
  
  // Add state for tracking screen size for automatic view switching
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Calculate minimum grid height based on reports and default canvas size
  const getGridHeight = useCallback((dashboard: Dashboard) => {
    const defaultHeight = currentViewType === 'desktop' ? 30 : 40; // Default canvas: Desktop 100x30, Mobile 20x40
    const rowHeight = currentViewType === 'mobile' ? 5 : 8;
    
    if (!dashboard.reports || dashboard.reports.length === 0) {
      return defaultHeight * rowHeight; // Return default canvas size when empty
    }
    
    // Find the maximum Y + height position from all reports
    const maxBottomPosition = Math.max(
      defaultHeight, // Never go smaller than default canvas
      ...dashboard.reports.map(report => report.layout.y + report.layout.h)
    );
    
    return maxBottomPosition * rowHeight;
  }, [currentViewType]);

  // Add breakpoint detection for automatic view switching
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setScreenWidth(newWidth);
      
      // Smart auto-switching: Only switch when both views exist and screen size changes
      const shouldBeMobile = newWidth < 768; // Switch to mobile view at 768px and below
      
      if (shouldBeMobile && currentViewType === 'desktop') {
        // Only switch if there are dashboards configured for both views
        const hasIndependentViews = dashboards.some(d => d.desktopConfigured && d.mobileConfigured);
        const hasMobileOnlyViews = dashboards.some(d => d.mobileConfigured && !d.desktopConfigured);
        
        if (hasIndependentViews || hasMobileOnlyViews) {
          setCurrentViewType('mobile');
        }
      } else if (!shouldBeMobile && currentViewType === 'mobile') {
        // Only switch if there are dashboards configured for both views
        const hasIndependentViews = dashboards.some(d => d.desktopConfigured && d.mobileConfigured);
        const hasDesktopOnlyViews = dashboards.some(d => d.desktopConfigured && !d.mobileConfigured);
        
        if (hasIndependentViews || hasDesktopOnlyViews) {
          setCurrentViewType('desktop');
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentViewType, dashboards]);
  
  // Refs for debouncing auto-save
  const dashboardTitleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reportTitleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load dashboards from database
  useEffect(() => {
    if (businessId) {
      loadDashboards();
    }
  }, [businessId]);

  // Reload dashboards when view type changes
  useEffect(() => {
    if (businessId) {
      loadDashboards();
    }
  }, [currentViewType]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (dashboardTitleTimeoutRef.current) {
        clearTimeout(dashboardTitleTimeoutRef.current);
      }
      if (reportTitleTimeoutRef.current) {
        clearTimeout(reportTitleTimeoutRef.current);
      }
    };
  }, []);

  const loadDashboards = async (viewType?: 'desktop' | 'mobile') => {
    if (!businessId) return;
    
    try {
      setLoading(true);
              const dashboardsData = await getKPIDashboards(businessId);
        
        // Load reports for each dashboard
        const dashboardsWithReports: Dashboard[] = await Promise.all(
          dashboardsData.map(async (d) => {
            const reportsData = await getDashboardReports(d.id, viewType || currentViewType);
            const defaultDateRange = (d.default_date_range as DateRangeOption) || 'this_month';
          
          // Update report data with real data based on the default date range
          const reports: Report[] = await Promise.all(
            reportsData.map(async (r) => ({
              id: r.id,
              type: r.report_type,
              title: r.report_title,
              data: r.report_type === 'title' 
                ? r.report_data // Preserve saved title data
                : await getFilteredReportData(r.report_type, defaultDateRange, businessId),
              layout: {
                x: r.layout_x,
                y: r.layout_y,
                w: r.layout_w,
                h: r.layout_h
              }
            }))
          );
          
          return {
            id: d.id,
            name: d.title,
            createdAt: new Date(d.created_at),
            reports,
            dateRange: defaultDateRange, // Use the saved default date range
            defaultDateRange: defaultDateRange,
            createdDeviceType: d.created_device_type,
            desktopConfigured: d.desktop_configured,
            mobileConfigured: d.mobile_configured
          };
        })
      );
      
      setDashboards(dashboardsWithReports);
      
      // Initialize reportTitleTexts state for all title elements
      const titleTexts: {[key: string]: string} = {};
      dashboardsWithReports.forEach(dashboard => {
        dashboard.reports?.forEach(report => {
          if (report.type === 'title' && report.data?.text) {
            titleTexts[report.id] = report.data.text;
          }
        });
      });
      setReportTitleTexts(titleTexts);
    } catch (error) {
      console.error('Error loading dashboards:', error);
      toast.error('Failed to load dashboards');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDashboard = async () => {
    if (!businessId) {
      toast.error('Business not selected');
      return;
    }

    if (dashboardName.trim()) {
      try {
        // Pass current view type to ensure dashboard is only created for current view
        const newDashboard = await createKPIDashboard({
          business_id: businessId,
          title: dashboardName.trim()
        }, currentViewType);
        
        const formattedDashboard: Dashboard = {
          id: newDashboard.id,
          name: newDashboard.title,
          createdAt: new Date(newDashboard.created_at),
          dateRange: 'this_month', // Default to this month
          defaultDateRange: 'this_month', // Default to this month
          createdDeviceType: newDashboard.created_device_type,
          desktopConfigured: newDashboard.desktop_configured,
          mobileConfigured: newDashboard.mobile_configured
        };
        
        setDashboards(prev => [...prev, formattedDashboard]);
      setDashboardName('');
      setIsModalOpen(false);
        toast.success(`Dashboard created for ${currentViewType} view only`);
      } catch (error) {
        console.error('Error creating dashboard:', error);
        toast.error('Failed to create dashboard');
      }
    }
  };

  const handleDateRangeChange = async (dashboardId: string, newDateRange: DateRangeOption) => {
    if (!businessId) return;
    
    setDashboards(prev => prev.map(dashboard => {
      if (dashboard.id === dashboardId) {
        return {
          ...dashboard,
          dateRange: newDateRange
        };
      }
      return dashboard;
    }));

    // Update reports with new filtered data
    const dashboard = dashboards.find(d => d.id === dashboardId);
    if (dashboard?.reports) {
      const updatedReports = await Promise.all(
        dashboard.reports.map(async (report) => ({
          ...report,
          data: report.type === 'title' 
            ? report.data // Preserve title data when changing date range
            : report.type === 'stock_alert'
            ? report.data // Preserve stock alert data - it's live and date-independent
            : await getFilteredReportData(report.type, newDateRange, businessId)
        }))
      );

      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            reports: updatedReports
          };
        }
        return dashboard;
      }));
    }
    
    toast.success(`Date range updated to ${getDateRangeLabel(newDateRange)}`);
  };

  const handleSetDefaultDateRange = async (dashboardId: string, newDefaultDateRange: DateRangeOption) => {
    try {
      await updateDashboardDefaultDateRange(dashboardId, newDefaultDateRange);
      
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            defaultDateRange: newDefaultDateRange
          };
        }
        return dashboard;
      }));
      
      toast.success(`Default date range set to ${getDateRangeLabel(newDefaultDateRange)}`);
    } catch (error) {
      console.error('Error setting default date range:', error);
      toast.error('Failed to save default date range');
    }
  };

  // Function to fetch real sales data from the database
  const fetchSalesData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query bookings within the date range, excluding cancelled bookings
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('subtotal, discount_amount, status, end_date')
        .eq('business_id', businessId)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0])
        .neq('status', 'cancelled');

      if (error) {
        console.error('Error fetching sales data:', error);
        throw error;
      }

      // Calculate total sales after discounts (same logic as Sales.tsx)
      const totalAfterDiscounts = (bookings || []).reduce((sum, booking) => {
        const subtotal = booking.subtotal || 0;
        const discountAmount = booking.discount_amount || 0;
        return sum + (subtotal - discountAmount);
      }, 0);

      return {
        value: `${totalAfterDiscounts >= 0 ? '+' : ''}$${totalAfterDiscounts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        label: `Total Sales After Discounts`
      };
    } catch (error) {
      console.error('Error fetching sales data:', error);
      // Return fallback data on error
      return {
        value: '+$0.00',
        label: `Total Sales After Discounts`
      };
    }
  };

  // Function to fetch real upsells data from the database
  const fetchUpsellsData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query bookings within the date range, excluding cancelled bookings, only those with upsells data
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('upsells, status, end_date')
        .eq('business_id', businessId)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0])
        .neq('status', 'cancelled')
        .not('upsells', 'is', null);

      if (error) {
        console.error('Error fetching upsells data:', error);
        throw error;
      }

      // Calculate total upsells (same logic as Sales.tsx)
      const totalUpsells = (bookings || []).reduce((sum, booking) => {
        return sum + (booking.upsells || 0);
      }, 0);

      return {
        value: `${totalUpsells >= 0 ? '+' : ''}$${totalUpsells.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        label: `Total Upsells`
      };
    } catch (error) {
      console.error('Error fetching upsells data:', error);
      // Return fallback data on error
      return {
        value: '+$0.00',
        label: `Total Upsells`
      };
    }
  };

  // Function to fetch real closed sales data from the database
  const fetchClosedSalesData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query only completed bookings within the date range (same logic as ProfitLoss.tsx)
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('subtotal, discount_amount, status, end_date')
        .eq('business_id', businessId)
        .eq('status', 'completed') // Only completed bookings for closed sales
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0]);

      if (error) {
        console.error('Error fetching closed sales data:', error);
        throw error;
      }

      // Calculate total closed sales after discounts (same logic as ProfitLoss.tsx)
      const totalClosedSales = (bookings || []).reduce((sum, booking) => {
        const subtotal = booking.subtotal || 0;
        const discountAmount = booking.discount_amount || 0;
        return sum + (subtotal - discountAmount);
      }, 0);

      return {
        value: `${totalClosedSales >= 0 ? '+' : ''}$${totalClosedSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        label: `Closed Sales`
      };
    } catch (error) {
      console.error('Error fetching closed sales data:', error);
      // Return fallback data on error
      return {
        value: '+$0.00',
        label: `Closed Sales`
      };
    }
  };

  // Function to fetch real supplies purchased data from the database
  const fetchSuppliesPurchasedData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query stock transactions for purchases within the date range
      // Need to join with stock_items to filter by business_id since the view doesn't include it directly
      const { data: transactions, error } = await supabase
        .from('stock_transactions')
        .select(`
          quantity,
          price,
          created_at,
          type,
          stock_items!inner(business_id)
        `)
        .eq('type', 'purchase') // Only purchase transactions
        .eq('stock_items.business_id', businessId) // Filter by business
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching supplies purchased data:', error);
        throw error;
      }

      // Calculate total supplies purchased cost (same logic as SuppliesUsage.tsx)
      const totalSuppliesPurchased = (transactions || []).reduce((sum, transaction) => {
        const quantity = transaction.quantity || 0;
        const price = transaction.price || 0;
        const cost = quantity * price;
        return sum + cost;
      }, 0);

      return {
        value: `-$${totalSuppliesPurchased.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        label: `Supplies Purchased`
      };
    } catch (error) {
      console.error('Error fetching supplies purchased data:', error);
      // Return fallback data on error
      return {
        value: '-$0.00',
        label: `Supplies Purchased`
      };
    }
  };

  // Helper function for precise date filtering (matches ProfitLoss.tsx)
  const isDateInRange = (date: string | null | undefined, rangeOption: DateRangeOption): boolean => {
    if (!date) return false;
    
    // Convert the booking end date to UTC midnight
    const targetDate = new Date(date + 'T00:00:00Z');
    const now = new Date();
    const utcNow = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));

    switch (rangeOption) {
      case 'this_month': {
        const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 1));
        const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 0, 23, 59, 59, 999));
        return targetDate >= start && targetDate <= end;
      }
      case 'last_month': {
        const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() - 1, 1));
        const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 0, 23, 59, 59, 999));
        return targetDate >= start && targetDate <= end;
      }
      case 'next_month': {
        const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 1));
        const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 2, 0, 23, 59, 59, 999));
        return targetDate >= start && targetDate <= end;
      }
      case 'this_week': {
        const startOfWeek = (date: Date) => {
          const d = new Date(date);
          const day = d.getUTCDay();
          const diff = d.getUTCDate() - day;
          return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), diff));
        };
        const start = startOfWeek(utcNow);
        const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000 + 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000 + 999);
        return targetDate >= start && targetDate <= end;
      }
      case 'last_week': {
        const startOfWeek = (date: Date) => {
          const d = new Date(date);
          const day = d.getUTCDay();
          const diff = d.getUTCDate() - day - 7;
          return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), diff));
        };
        const start = startOfWeek(utcNow);
        const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000 + 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000 + 999);
        return targetDate >= start && targetDate <= end;
      }
      case 'next_week': {
        const startOfWeek = (date: Date) => {
          const d = new Date(date);
          const day = d.getUTCDay();
          const diff = d.getUTCDate() - day + 7;
          return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), diff));
        };
        const start = startOfWeek(utcNow);
        const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000 + 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000 + 999);
        return targetDate >= start && targetDate <= end;
      }
      default:
        return false;
    }
  };

  // Function to fetch real job materials data from the database (matches ProfitLoss.tsx calculation)
  const fetchJobMaterialsData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Get job materials costs from booking_materials table (same as ProfitLoss.tsx)
      const { data: materialsData, error: materialsError } = await supabase
        .from('booking_materials')
        .select(`
          id,
          quantity,
          cost,
          total_cost,
          created_at,
          material:material_id (
            id,
            name,
            description,
            unit,
            category:category_id (
              id,
              name
            )
          ),
          booking:booking_id (
            id,
            status,
            end_date,
            business_id
          )
        `)
        .eq('booking.business_id', businessId)
        .eq('booking.status', 'completed')
        .gte('booking.end_date', startDate.toISOString().split('T')[0])
        .lte('booking.end_date', endDate.toISOString().split('T')[0]);

      if (materialsError) {
        console.error('Error fetching materials for job materials:', materialsError);
        throw materialsError;
      }

      // Apply additional date filtering (same as ProfitLoss.tsx)
      const filteredMaterials = (materialsData || []).filter(material => {
        return material.booking?.status === 'completed' && 
               material.booking?.end_date && 
               isDateInRange(material.booking.end_date, dateRange);
      });

      // Calculate total job materials costs (sum of all total_cost from booking_materials)
      const totalJobMaterials = filteredMaterials.reduce((sum, material) => {
        return sum + (material.total_cost || 0);
      }, 0);

      return {
        value: `-$${totalJobMaterials.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        label: `Total Job Materials`
      };
    } catch (error) {
      console.error('Error fetching job materials data:', error);
      // Return fallback data on error
      return {
        value: '-$0.00',
        label: `Total Job Materials`
      };
    }
  };

  // Function to fetch real labour data from the database
  const fetchLabourData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Get total labor costs (exact same logic as ProfitLoss.tsx)
      // Fetch flat rate entries
      const { data: flatRateData, error: flatRateError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          completed_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'flat_rate')
        .gte('completed_at', startDate.toISOString())
        .lte('completed_at', endDate.toISOString());

      if (flatRateError) {
        console.error('Error fetching flat rate data for labour:', flatRateError);
        throw flatRateError;
      }

      // Fetch timeclock entries
      const { data: timeclockData, error: timeclockError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          clock_out,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'timeclock')
        .gte('clock_out', startDate.toISOString())
        .lte('clock_out', endDate.toISOString());

      if (timeclockError) {
        console.error('Error fetching timeclock data for labour:', timeclockError);
        throw timeclockError;
      }

      // Combine both types of payroll data
      const combinedLaborData = [
        ...(flatRateData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || []),
        ...(timeclockData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || [])
      ];

      const totalLaborCosts = combinedLaborData.reduce((sum, entry) => {
        return sum + (entry.total_earnings || 0);
      }, 0);

      return {
        value: `-$${totalLaborCosts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        label: `Total Labour Costs`
      };
    } catch (error) {
      console.error('Error fetching labour data:', error);
      // Return fallback data on error
      return {
        value: '-$0.00',
        label: `Total Labour Costs`
      };
    }
  };

  // Function to fetch total jobs data from the database
  const fetchTotalJobsData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Get all bookings within the date range regardless of status
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('id, end_date, status')
        .eq('business_id', businessId)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0]);

      if (error) {
        console.error('Error fetching total jobs data:', error);
        throw error;
      }

      // Apply additional date filtering (same as other reports)
      const filteredBookings = (bookings || []).filter(booking => {
        return booking.end_date && isDateInRange(booking.end_date, dateRange);
      });

      const totalJobs = filteredBookings.length;

      return {
        value: totalJobs.toString(),
        label: `Total Jobs Booked`
      };
    } catch (error) {
      console.error('Error fetching total jobs data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Total Jobs Booked`
      };
    }
  };

  // Function to fetch completed jobs data from the database
  const fetchCompletedJobsData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Get only completed bookings within the date range
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('id, end_date, status')
        .eq('business_id', businessId)
        .eq('status', 'completed')
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0]);

      if (error) {
        console.error('Error fetching completed jobs data:', error);
        throw error;
      }

      // Apply additional date filtering (same as other reports)
      const filteredCompletedBookings = (bookings || []).filter(booking => {
        return booking.status === 'completed' && 
               booking.end_date && 
               isDateInRange(booking.end_date, dateRange);
      });

      const completedJobs = filteredCompletedBookings.length;

      return {
        value: completedJobs.toString(),
        label: `Completed Jobs`
      };
    } catch (error) {
      console.error('Error fetching completed jobs data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Completed Jobs`
      };
    }
  };

  // Function to fetch real net profit data from the database (matches ProfitLoss.tsx calculation)
  const fetchNetProfitData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // 1. Get total revenue after discounts from completed bookings (same as ProfitLoss.tsx)
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('subtotal, discount_amount, status, end_date')
        .eq('business_id', businessId)
        .eq('status', 'completed')
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0]);

      if (bookingsError) {
        console.error('Error fetching bookings for net profit:', bookingsError);
        throw bookingsError;
      }

      // Calculate total revenue after discounts
      const totalRevenue = (bookings || []).reduce((sum, booking) => {
        const subtotal = booking.subtotal || 0;
        const discountAmount = booking.discount_amount || 0;
        return sum + (subtotal - discountAmount);
      }, 0);

      // 2. Get total labor costs (exact same logic as ProfitLoss.tsx)
      // Fetch flat rate entries
      const { data: flatRateData, error: flatRateError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          completed_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'flat_rate')
        .gte('completed_at', startDate.toISOString())
        .lte('completed_at', endDate.toISOString());

      if (flatRateError) {
        console.error('Error fetching flat rate data for net profit:', flatRateError);
        throw flatRateError;
      }

      // Fetch timeclock entries
      const { data: timeclockData, error: timeclockError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          clock_out,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'timeclock')
        .gte('clock_out', startDate.toISOString())
        .lte('clock_out', endDate.toISOString());

      if (timeclockError) {
        console.error('Error fetching timeclock data for net profit:', timeclockError);
        throw timeclockError;
      }

      // Combine both types of payroll data
      const combinedLaborData = [
        ...(flatRateData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || []),
        ...(timeclockData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || [])
      ];

      const totalLaborCosts = combinedLaborData.reduce((sum, entry) => {
        return sum + (entry.total_earnings || 0);
      }, 0);

      // 3. Get total supply costs from stock purchases (same logic as ProfitLoss.tsx)
      const { data: stockTransactions, error: stockError } = await supabase
        .from('stock_transactions')
        .select(`
          quantity,
          price,
          created_at,
          type,
          stock_items!inner(business_id)
        `)
        .eq('type', 'purchase')
        .eq('stock_items.business_id', businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (stockError) {
        console.error('Error fetching stock data for net profit:', stockError);
        throw stockError;
      }

      const totalSupplyCosts = (stockTransactions || []).reduce((sum, transaction) => {
        const quantity = Math.abs(Number(transaction.quantity) || 0);
        const price = Number(transaction.price) || 0;
        return sum + (quantity * price);
      }, 0);

      // 4. Get fixed expenses from operating costs (same logic as ProfitLoss.tsx)
      const { data: operatingCosts, error: operatingCostsError } = await supabase
        .from('operating_costs')
        .select('amount')
        .eq('business_id', businessId);

      if (operatingCostsError) {
        console.error('Error fetching operating costs for net profit:', operatingCostsError);
        throw operatingCostsError;
      }

      const fixedExpenses = (operatingCosts || []).reduce((sum, expense) => {
        return sum + (Number(expense.amount) || 0);
      }, 0);

      // 5. Calculate Net Profit (same formula as ProfitLoss.tsx)
      const netProfit = totalRevenue - (totalLaborCosts + totalSupplyCosts + fixedExpenses);

      return {
        value: `${netProfit >= 0 ? '+' : ''}$${netProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        label: `Bottom Line Profit`
      };
    } catch (error) {
      console.error('Error fetching net profit data:', error);
      // Return fallback data on error
      return {
        value: '+$0.00',
        label: `Bottom Line Profit`
      };
    }
  };

  // Function to fetch stock alert data - live current stock health
  const fetchStockAlertData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string; status: 'healthy' | 'low' }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      // Query all stock items for the business (excluding deleted items)
      const { data: stockItems, error } = await supabase
        .from('stock_items')
        .select('id, name, quantity, reorder_point, supplier_id, max_quantity, acquisition_cost, unit')
        .eq('business_id', businessId)
        .neq('is_deleted', true);

      if (error) {
        console.error('Error fetching stock data:', error);
        throw error;
      }

      // Update state with stock items for supplier order calculations
      setStockItems(stockItems || []);

      // Check if there are any items below reorder point
      // Note: We count the number of distinct items that are low, not the total quantity needed
      const lowStockItems = (stockItems || []).filter(item => 
        item.quantity <= item.reorder_point
      );

      const totalItems = stockItems?.length || 0;
      const lowStockCount = lowStockItems.length; // Count of distinct items needing reorder

      if (lowStockCount > 0) {
        // Low stock situation - show count of items needing reorder
        return {
          value: lowStockCount.toString(),
          label: `Item${lowStockCount > 1 ? 's' : ''} Need${lowStockCount === 1 ? 's' : ''} Reorder`,
          status: 'low'
        };
      } else {
        // Healthy stock situation - show 0 (no items need reordering)
        return {
          value: '0',
          label: 'Items Need Reorder',
          status: 'healthy'
        };
      }
    } catch (error) {
      console.error('Error fetching stock alert data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: 'Stock Status Unknown',
        status: 'healthy'
      };
    }
  };

  // Function to fetch online booking funnel data
  const fetchOnlineBookingVolumeData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string; funnelData?: any }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query booking step progression within the date range
      const { data: stepData, error } = await supabase
        .from('booking_step_progression')
        .select('step, session_id')
        .eq('business_id', businessId)
        .gte('reached_at', startDate.toISOString())
        .lte('reached_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching booking funnel data:', error);
        throw error;
      }

      // Process funnel data
      const stepCounts = {
        location: new Set(),
        category: new Set(),
        service: new Set(),
        datetime: new Set(),
        customer: new Set(),
        completed: new Set()
      };

      // Count unique sessions that reached each step
      (stepData || []).forEach(item => {
        if (stepCounts[item.step as keyof typeof stepCounts]) {
          stepCounts[item.step as keyof typeof stepCounts].add(item.session_id);
        }
      });

      const funnel = [
        { step: 'Location', count: stepCounts.location.size, icon: 'MapPin' },
        { step: 'Category', count: stepCounts.category.size, icon: 'Tag' },
        { step: 'Service', count: stepCounts.service.size, icon: 'Wrench' },
        { step: 'Date & Time', count: stepCounts.datetime.size, icon: 'Calendar' },
        { step: 'Customer Info', count: stepCounts.customer.size, icon: 'User' },
        { step: 'Completed', count: stepCounts.completed.size, icon: 'CheckCircle' }
      ];

      const totalStarts = stepCounts.location.size;
      const totalCompletions = stepCounts.completed.size;
      const conversionRate = totalStarts > 0 ? ((totalCompletions / totalStarts) * 100).toFixed(1) : '0.0';

      return {
        value: `${conversionRate}%`,
        label: `Conversion Rate (${totalCompletions}/${totalStarts})`,
        funnelData: funnel
      };
    } catch (error) {
      console.error('Error fetching booking funnel data:', error);
      // Return fallback data on error
      return {
        value: '0.0%',
        label: 'Conversion Rate (0/0)',
        funnelData: [
          { step: 'Location', count: 0, icon: 'MapPin' },
          { step: 'Category', count: 0, icon: 'Tag' },
          { step: 'Service', count: 0, icon: 'Wrench' },
          { step: 'Date & Time', count: 0, icon: 'Calendar' },
          { step: 'Customer Info', count: 0, icon: 'User' },
          { step: 'Completed', count: 0, icon: 'CheckCircle' }
        ]
      };
    }
  };

  // Load suppliers for stock alert functionality
  const loadSuppliers = useCallback(async () => {
    if (!businessId) return;
    
    try {
      const data = await getSuppliers(businessId);
      setSuppliers(data);
    } catch (err) {
      console.error('Error loading suppliers:', err);
    }
  }, [businessId]);

  // Load pending orders for stock alert functionality
  const loadPendingOrders = useCallback(async () => {
    if (!businessId) return;
    
    try {
      const pendingItems = await getPendingOrdersForItems(businessId);
      
      // Convert to a Map for easy lookup
      const itemMap = new Map<string, number>();
      pendingItems.forEach(item => {
        itemMap.set(item.itemId, item.quantityOrdered);
      });
      
      setPendingOrderItems(itemMap);
      
    } catch (err) {
      console.error('Error loading pending orders:', err);
    }
  }, [businessId]);

  // Calculate supplier orders for stock alert
  const calculateSupplierOrders = useCallback(() => {
    if (!stockItems.length || !suppliers.length) return;

    // Filter items that are below reorder point and have a supplier
    const itemsToOrder = stockItems.filter(item => {
      const isLowStock = item.quantity <= item.reorder_point;
      const hasSupplier = !!item.supplier_id;
      const canAddMoreStock = item.max_quantity > item.quantity;
      
      // Check if the item is already in a pending order
      const pendingQuantity = pendingOrderItems.get(item.id) || 0;
      const projectedQuantity = item.quantity + pendingQuantity;
      const stillNeedsOrdering = projectedQuantity <= item.reorder_point;
      
      return isLowStock && hasSupplier && canAddMoreStock && stillNeedsOrdering;
    });
    
    // Group by supplier
    const ordersBySupplier: Record<string, SupplierOrder> = {};
    
    itemsToOrder.forEach(item => {
      if (!item.supplier_id) return;
      
      const supplier = suppliers.find(s => s.id === item.supplier_id);
      if (!supplier) return;
      
      // Calculate how much quantity to order considering pending orders
      const pendingQuantity = pendingOrderItems.get(item.id) || 0;
      const effectiveQuantity = item.quantity + pendingQuantity;
      const quantityToOrder = item.max_quantity - effectiveQuantity;
      
      if (quantityToOrder <= 0) return;
      
      const totalCost = quantityToOrder * item.acquisition_cost;
      
      if (!ordersBySupplier[supplier.id]) {
        ordersBySupplier[supplier.id] = {
          supplier,
          items: [],
          totalCost: 0,
          itemCount: 0
        };
      }
      
      ordersBySupplier[supplier.id].items.push({
        item,
        quantityToOrder,
        totalCost
      });
      
      ordersBySupplier[supplier.id].totalCost += totalCost;
      ordersBySupplier[supplier.id].itemCount += 1;
    });
    
    // Convert to array and sort by supplier name
    const ordersArray = Object.values(ordersBySupplier)
      .sort((a, b) => {
        const aName = `${a.supplier.first_name} ${a.supplier.last_name}`.trim();
        const bName = `${b.supplier.first_name} ${b.supplier.last_name}`.trim();
        return aName.localeCompare(bName);
      });
    
    setSupplierOrders(ordersArray);
  }, [stockItems, suppliers, pendingOrderItems]);

  // Handle placing an order
  const handlePlaceOrder = async (supplierId: string) => {
    const orderGroup = supplierOrders.find(o => o.supplier.id === supplierId);
    if (!orderGroup || !businessId) {
      console.error('Order group not found for supplier', supplierId);
      return;
    }
    
    try {
      // Create order number and get current date
      const orderNumber = `PO-${Date.now().toString().slice(-6)}`;
      
      // Prepare order items for the database
      const dbOrderItems: DBOrderItem[] = orderGroup.items.map(item => ({
        item_id: item.item.id,
        quantity: item.quantityToOrder,
        unit_price: item.item.acquisition_cost,
        total_price: item.totalCost,
        item_name: item.item.name,
        item_unit: item.item.unit
      }));
      
      // Create the order in the database
      await createOrder(
        {
          business_id: businessId,
          supplier_id: orderGroup.supplier.id,
          order_number: orderNumber,
          total_amount: orderGroup.totalCost,
          status: 'pending'
        },
        dbOrderItems
      );
      
      // Show success notification
      toast.success(`Order ${orderNumber} placed successfully!`);
      
      // Refresh data
      await loadPendingOrders();
      calculateSupplierOrders();
      
    } catch (err) {
      console.error('Error sending order:', err);
             toast.error('Failed to place order: ' + (err instanceof Error ? err.message : 'Unknown error'));
     }
   };

  // Load suppliers and pending orders when business changes
  useEffect(() => {
    if (businessId) {
      loadSuppliers();
      loadPendingOrders();
    }
  }, [businessId, loadSuppliers, loadPendingOrders]);

  // Calculate supplier orders when data changes
  useEffect(() => {
    if (stockItems.length > 0 && suppliers.length > 0) {
      calculateSupplierOrders();
    }
  }, [stockItems, suppliers, pendingOrderItems, calculateSupplierOrders]);

  // Generate filtered report data based on date range
  const getFilteredReportData = async (reportType: string, dateRange: DateRangeOption, businessId: string) => {
    if (reportType === 'sales') {
      // Use real sales data for sales reports
      return await fetchSalesData(businessId, dateRange);
    }
    
    if (reportType === 'upsells') {
      // Use real upsells data for upsells reports
      return await fetchUpsellsData(businessId, dateRange);
    }
    
    if (reportType === 'closed_sales') {
      // Use real closed sales data for closed sales reports
      return await fetchClosedSalesData(businessId, dateRange);
    }
    
    if (reportType === 'supplies_purchased') {
      // Use real supplies purchased data for supplies purchased reports
      return await fetchSuppliesPurchasedData(businessId, dateRange);
    }
    
    if (reportType === 'net_profit') {
      // Use real net profit data for net profit reports
      return await fetchNetProfitData(businessId, dateRange);
    }
    
    if (reportType === 'labour') {
      // Use real labour data for labour reports
      return await fetchLabourData(businessId, dateRange);
    }
    
    if (reportType === 'job_materials') {
      // Use real job materials data for job materials reports
      return await fetchJobMaterialsData(businessId, dateRange);
    }
    
    if (reportType === 'total_jobs') {
      // Use real total jobs data for total jobs reports
      return await fetchTotalJobsData(businessId, dateRange);
    }
    
    if (reportType === 'completed_jobs') {
      // Use real completed jobs data for completed jobs reports
      return await fetchCompletedJobsData(businessId, dateRange);
    }

    if (reportType === 'stock_alert') {
      // Use real stock alert data for stock alert reports
      return await fetchStockAlertData(businessId, dateRange);
    }

    if (reportType === 'online_booking_volume') {
      // Use real online booking volume data for online booking volume reports
      return await fetchOnlineBookingVolumeData(businessId, dateRange);
    }
    
    if (reportType === 'title') {
      // For title elements, return text data instead of value/label
      return {
        text: 'Dashboard Title',
        isTitle: true
      };
    }
    
    // For other report types, use simulated data for now
    const baseValue = 24567;
    const multiplier = dateRange.includes('month') ? 1 : 0.25; // Weeks are roughly 1/4 of month
    const randomFactor = 0.8 + Math.random() * 0.4; // Random variation between 0.8 and 1.2
    
    const filteredValue = Math.round(baseValue * multiplier * randomFactor);
    
    return {
      value: `$${filteredValue.toLocaleString()}`,
      label: `Total Sales After Discounts`
    };
  };

  const handleReportClick = async (reportType: string, reportTitle: string) => {
    if (!selectedDashboardId || !businessId) return;

    try {
      const selectedDashboard = dashboards.find(d => d.id === selectedDashboardId);
      const dateRange = selectedDashboard?.dateRange || 'this_month';
      const filteredData = await getFilteredReportData(reportType, dateRange, businessId);
      
      const existingReports = selectedDashboard?.reports || [];
      const nextPosition = existingReports.length;

      // Set different default sizes for different report types based on view type
      let defaultWidth, defaultHeight;
      
      if (currentViewType === 'mobile') {
        // Mobile: 20-column grid system with 18px row height
        if (reportType === 'stock_alert') {
          defaultWidth = 20; // Full width for stock alert
          defaultHeight = 40; // Taller for stock alert content
        } else if (reportType === 'online_booking_volume') {
          defaultWidth = 20; // Full width for funnel display
          defaultHeight = 32; // Tall enough for funnel steps
        } else {
          defaultWidth = 10; // Half width for others
          defaultHeight = 24; // Standard height
        }
      } else {
        // Desktop: 100-column grid system with 10px row height
        if (reportType === 'stock_alert') {
          defaultWidth = 40; // 40% width for stock alert
          defaultHeight = 35; // Taller for stock alert content
        } else if (reportType === 'online_booking_volume') {
          defaultWidth = 30; // 30% width for funnel display
          defaultHeight = 30; // Tall enough for funnel steps
        } else {
          defaultWidth = 20; // 20% width for others
          defaultHeight = 25; // Standard height
        }
      }

      const reportData = {
        dashboard_id: selectedDashboardId,
        business_id: businessId,
        report_type: reportType,
        report_title: reportTitle,
        layout_x: currentViewType === 'mobile' ? (nextPosition * defaultWidth) % 20 : (nextPosition * defaultWidth) % 100, // Mobile: use 20-column grid, Desktop: use 100-column grid
        layout_y: currentViewType === 'mobile' ? Math.floor((nextPosition * defaultWidth) / 20) * defaultHeight : Math.floor((nextPosition * defaultWidth) / 100) * defaultHeight, // Mobile: wrap after 20 columns, Desktop: wrap after 100
        layout_w: defaultWidth, // Variable width based on report type
        layout_h: defaultHeight, // Variable height based on report type
        view_type: currentViewType, // Add current view type
        report_data: filteredData
      };

      const createdReport = await createDashboardReport(reportData);

      const newReport: Report = {
        id: createdReport.id,
        type: createdReport.report_type,
        title: createdReport.report_title,
        data: createdReport.report_data,
        layout: {
          x: createdReport.layout_x,
          y: createdReport.layout_y,
          w: createdReport.layout_w,
          h: createdReport.layout_h
        }
      };

      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === selectedDashboardId) {
          return {
            ...dashboard,
            reports: [...(dashboard.reports || []), newReport]
          };
        }
        return dashboard;
      }));

      // If it's a title element, initialize its text in our state
      if (reportType === 'title') {
        const titleText = 'text' in filteredData ? filteredData.text : 'Dashboard Title';
        setReportTitleTexts(prev => ({
          ...prev,
          [createdReport.id]: titleText
        }));
      }

      setIsAddReportsModalOpen(false);
      setSelectedDashboardId(null);
      toast.success(`${reportTitle} ${reportType === 'title' ? 'element' : 'report'} added to ${currentViewType} view`);
    } catch (error) {
      console.error('Error adding report:', error);
      toast.error('Failed to add report');
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setDashboardName('');
  };

  const handleAddReportsClick = (dashboardId: string) => {
    setSelectedDashboardId(dashboardId);
    setIsAddReportsModalOpen(true);
  };

  const handleAddReportsModalClose = () => {
    setIsAddReportsModalOpen(false);
    setSelectedDashboardId(null);
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    // Cancel any ongoing title edits when toggling edit mode
    setEditingTitleId(null);
    setEditingTitle('');
    // Clear report title texts when exiting edit mode
    if (isEditMode) {
      setReportTitleTexts({});
    }
  };

  const handleStartTitleEdit = (dashboardId: string, currentTitle: string) => {
    setEditingTitleId(dashboardId);
    setEditingTitle(currentTitle);
  };

  // Auto-save dashboard title with debouncing
  const autoSaveDashboardTitle = useCallback(async (dashboardId: string, title: string) => {
    if (!title.trim()) {
      return; // Don't save empty titles
    }

    try {
      await updateKPIDashboard(dashboardId, { title: title.trim() });
      
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            name: title.trim()
          };
        }
        return dashboard;
      }));
    } catch (error) {
      console.error('Error updating dashboard title:', error);
      toast.error('Failed to update dashboard name');
    }
  }, []);

  const handleTitleChange = (dashboardId: string, newTitle: string) => {
    setEditingTitle(newTitle);
    
    // Clear existing timeout
    if (dashboardTitleTimeoutRef.current) {
      clearTimeout(dashboardTitleTimeoutRef.current);
    }
    
    // Set new timeout for auto-save (500ms delay)
    dashboardTitleTimeoutRef.current = setTimeout(() => {
      autoSaveDashboardTitle(dashboardId, newTitle);
    }, 500);
  };

  const handleCancelTitleEdit = () => {
    // Clear any pending auto-save
    if (dashboardTitleTimeoutRef.current) {
      clearTimeout(dashboardTitleTimeoutRef.current);
    }
    setEditingTitleId(null);
    setEditingTitle('');
  };

  // Click outside handler for title editing
  const titleInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingTitleId && titleInputRef.current && !titleInputRef.current.contains(event.target as Node)) {
        handleCancelTitleEdit();
      }
    };

    if (editingTitleId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [editingTitleId]);

  // Auto-save report title with debouncing
  const autoSaveReportTitle = useCallback(async (dashboardId: string, reportId: string, titleText: string) => {
    if (!titleText.trim()) {
      return; // Don't save empty titles
    }

    try {
      // Update the database with the new title text
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const updatedData = {
        text: titleText.trim(),
        isTitle: true
      };

      const { error } = await supabase
        .from('kpi_dashboard_reports')
        .update({ report_data: updatedData })
        .eq('id', reportId);

      if (error) {
        console.error('Error updating report title in database:', error);
        throw error;
      }

      // Update the report data in the dashboard state
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            reports: dashboard.reports?.map(report => {
              if (report.id === reportId) {
                return {
                  ...report,
                  data: updatedData
                };
              }
              return report;
            })
          };
        }
        return dashboard;
      }));
    } catch (error) {
      console.error('Error updating report title:', error);
      toast.error('Failed to update title: ' + (error as Error).message);
    }
  }, []);

  const handleReportTitleChange = (dashboardId: string, reportId: string, newText: string) => {
    // Update local state immediately
    setReportTitleTexts(prev => ({
      ...prev,
      [reportId]: newText
    }));
    
    // Clear existing timeout
    if (reportTitleTimeoutRef.current) {
      clearTimeout(reportTitleTimeoutRef.current);
    }
    
    // Set new timeout for auto-save (500ms delay)
    reportTitleTimeoutRef.current = setTimeout(() => {
      autoSaveReportTitle(dashboardId, reportId, newText);
    }, 500);
  };

  const handleLayoutChange = async (layout: any[], dashboardId: string) => {
    try {
      // Update each report's layout in the database
      const dashboard = dashboards.find(d => d.id === dashboardId);
      if (!dashboard?.reports) return;

      const updatePromises = dashboard.reports.map(async (report) => {
        const layoutItem = layout.find(item => item.i === report.id);
        if (layoutItem && (
          layoutItem.x !== report.layout.x ||
          layoutItem.y !== report.layout.y ||
          layoutItem.w !== report.layout.w ||
          layoutItem.h !== report.layout.h
        )) {
          await updateDashboardReportLayout(report.id, {
            layout_x: layoutItem.x,
            layout_y: layoutItem.y,
            layout_w: layoutItem.w,
            layout_h: layoutItem.h
          });
        }
      });

      await Promise.all(updatePromises);

      // Update local state
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            reports: dashboard.reports?.map(report => {
              const layoutItem = layout.find(item => item.i === report.id);
              if (layoutItem) {
                return {
                  ...report,
                  layout: {
                    x: layoutItem.x,
                    y: layoutItem.y,
                    w: layoutItem.w,
                    h: layoutItem.h
                  }
                };
              }
              return report;
            })
          };
        }
        return dashboard;
      }));
    } catch (error) {
      console.error('Error updating layout:', error);
      toast.error('Failed to save layout changes');
    }
  };

  // Smart responsive sizing functions
  const getResponsiveSize = (layout: { w: number; h: number }) => {
    const { w, h } = layout;
    const area = w * h;
    
    // Calculate actual pixel dimensions
    const actualWidth = currentViewType === 'mobile' 
      ? (w / 20) * 400  // Mobile: 400px total width
      : (w / 100) * 1200; // Desktop: 1200px total width
    
    const actualHeight = currentViewType === 'mobile' 
      ? h * 5  // Mobile: 5px per row
      : h * 8; // Desktop: 8px per row
    
    return {
      width: actualWidth,
      height: actualHeight,
      area: actualWidth * actualHeight
    };
  };

  const getContentFontSize = (layout: { w: number; h: number }, contentLength: number = 10) => {
    const { width, height } = getResponsiveSize(layout);
    
    // Base size calculation on widget area and content length
    let baseSize = Math.sqrt(width * height) / 10; // Start with area-based sizing
    
    // Adjust for content length - longer content needs smaller font
    if (contentLength > 20) baseSize *= 0.8;
    else if (contentLength > 10) baseSize *= 0.9;
    
    // Apply view-specific constraints
    if (currentViewType === 'mobile') {
      baseSize = Math.max(8, Math.min(baseSize, 24)); // 8px to 24px range
    } else {
      baseSize = Math.max(10, Math.min(baseSize, 36)); // 10px to 36px range
    }
    
    // Convert to Tailwind classes
    if (baseSize <= 10) return { class: 'text-xs', size: 10 };
    if (baseSize <= 12) return { class: 'text-sm', size: 12 };
    if (baseSize <= 14) return { class: 'text-base', size: 14 };
    if (baseSize <= 16) return { class: 'text-lg', size: 16 };
    if (baseSize <= 18) return { class: 'text-xl', size: 18 };
    if (baseSize <= 20) return { class: 'text-2xl', size: 20 };
    if (baseSize <= 24) return { class: 'text-3xl', size: 24 };
    return { class: 'text-4xl', size: 28 };
  };

  const getTitleFontSize = (layout: { w: number; h: number }) => {
    const { width, height } = getResponsiveSize(layout);
    let titleSize = Math.sqrt(width * height) / 8; // Slightly larger than content
    
    if (currentViewType === 'mobile') {
      titleSize = Math.max(8, Math.min(titleSize, 16));
    } else {
      titleSize = Math.max(10, Math.min(titleSize, 20));
    }
    
    if (titleSize <= 8) return { class: 'text-xs', size: 8 };
    if (titleSize <= 10) return { class: 'text-xs', size: 10 };
    if (titleSize <= 12) return { class: 'text-sm', size: 12 };
    if (titleSize <= 14) return { class: 'text-sm', size: 14 };
    if (titleSize <= 16) return { class: 'text-base', size: 16 };
    return { class: 'text-lg', size: 18 };
  };

  const getIconSize = (layout: { w: number; h: number }) => {
    const { width, height } = getResponsiveSize(layout);
    let iconSize = Math.min(width, height) / 8; // Icon size based on smaller dimension
    
    if (currentViewType === 'mobile') {
      iconSize = Math.max(12, Math.min(iconSize, 24));
    } else {
      iconSize = Math.max(16, Math.min(iconSize, 32));
    }
    
    if (iconSize <= 12) return { class: 'w-3 h-3', size: 12 };
    if (iconSize <= 16) return { class: 'w-4 h-4', size: 16 };
    if (iconSize <= 20) return { class: 'w-5 h-5', size: 20 };
    if (iconSize <= 24) return { class: 'w-6 h-6', size: 24 };
    if (iconSize <= 28) return { class: 'w-7 h-7', size: 28 };
    return { class: 'w-8 h-8', size: 32 };
  };

  const getResponsivePadding = (layout: { w: number; h: number }) => {
    const { width, height } = getResponsiveSize(layout);
    const minDimension = Math.min(width, height);
    
    if (minDimension < 80) return 'p-1';      // Very small widgets
    if (minDimension < 120) return 'p-2';     // Small widgets  
    if (minDimension < 200) return 'p-3';     // Medium widgets
    return 'p-4';                             // Large widgets
  };

  const getResponsiveSpacing = (layout: { w: number; h: number }) => {
    const { width, height } = getResponsiveSize(layout);
    const minDimension = Math.min(width, height);
    
    if (minDimension < 80) return 'space-x-1 space-y-1';
    if (minDimension < 120) return 'space-x-2 space-y-1';
    if (minDimension < 200) return 'space-x-2 space-y-2';
    return 'space-x-3 space-y-2';
  };

  const handleDeleteDashboard = async (dashboardId: string) => {
    try {
      await deleteKPIDashboard(dashboardId);
    setDashboards(dashboards.filter(d => d.id !== dashboardId));
      toast.success('Dashboard deleted successfully');
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      toast.error('Failed to delete dashboard');
    }
  };

  const handleDeleteReport = async (dashboardId: string, reportId: string) => {
    try {
      await deleteDashboardReport(reportId);
      
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            reports: dashboard.reports?.filter(report => report.id !== reportId)
          };
        }
        return dashboard;
      }));
      toast.success('Report removed from dashboard');
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete report');
    }
  };

  const handleConfigureOppositeView = async (dashboardId: string, targetViewType: 'desktop' | 'mobile') => {
    try {
      setConfiguringView(true);
      
      const result = await configureOppositeView(dashboardId, targetViewType);
      
      // Update dashboard state with new configuration status
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            desktopConfigured: result.dashboard.desktop_configured,
            mobileConfigured: result.dashboard.mobile_configured
          };
        }
        return dashboard;
      }));
      
      // Reload the dashboard data to show the new reports
      await loadDashboards();
      
      toast.success(`${targetViewType === 'desktop' ? 'Desktop' : 'Mobile'} view configured successfully! Views are now independent - changes to one won't affect the other.`);
    } catch (error) {
      console.error('Error configuring opposite view:', error);
      toast.error(`Failed to configure ${targetViewType} view`);
    } finally {
      setConfiguringView(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f6f7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-[#f6f6f7] ${currentViewType === 'mobile' ? 'max-w-sm mx-auto border-x border-gray-300' : ''}`}>
      {/* Main Page Header - only show if no dashboards for current view */}
      {dashboards.filter(dashboard => {
        return currentViewType === 'desktop' ? dashboard.desktopConfigured : dashboard.mobileConfigured;
      }).length === 0 && !loading && (
        <div className={`bg-white border-b border-gray-200 ${currentViewType === 'mobile' ? 'px-4 py-3' : 'px-6 py-4'}`}>
          <div className={`flex items-center ${currentViewType === 'mobile' ? 'flex-col space-y-3' : 'justify-between'}`}>
            <div>
              <h1 className={`font-bold text-gray-900 ${currentViewType === 'mobile' ? 'text-xl text-center' : 'text-2xl'}`}>KPI Dashboard</h1>
            </div>
            <div className={`flex items-center ${currentViewType === 'mobile' ? 'space-x-2' : 'space-x-3'}`}>
              <button
                onClick={toggleEditMode}
                className={`${currentViewType === 'mobile' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} font-medium rounded-lg flex items-center space-x-1 transition-colors ${
                  isEditMode 
                    ? 'text-white bg-blue-600 hover:bg-blue-700' 
                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {isEditMode ? <Eye className={currentViewType === 'mobile' ? 'h-3 w-3' : 'h-4 w-4'} /> : <Edit3 className={currentViewType === 'mobile' ? 'h-3 w-3' : 'h-4 w-4'} />}
                <span>{isEditMode ? 'View Mode' : 'Edit'}</span>
              </button>
              {isEditMode && (
                <button 
                  onClick={() => toast('Please create a dashboard first, then click "Add Reports" on that dashboard')}
                  className={`${currentViewType === 'mobile' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-1`}
                  title={`Add reports to ${currentViewType} view only`}
                >
              <FileBarChart className={currentViewType === 'mobile' ? 'h-3 w-3' : 'h-4 w-4'} />
              <span>Add Reports</span>
            </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Global view switcher - show when there are multiple configured views */}
      {dashboards.some(d => d.desktopConfigured) && dashboards.some(d => d.mobileConfigured) && (
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">KPI Dashboard</h2>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={async () => {
                  setCurrentViewType('desktop');
                  await loadDashboards('desktop');
                }}
                className={`px-3 py-1 text-sm font-medium rounded-md flex items-center space-x-1 transition-colors ${
                  currentViewType === 'desktop'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Monitor className="h-4 w-4" />
                <span>Desktop</span>
              </button>
              <button
                onClick={async () => {
                  setCurrentViewType('mobile');
                  await loadDashboards('mobile');
                }}
                className={`px-3 py-1 text-sm font-medium rounded-md flex items-center space-x-1 transition-colors ${
                  currentViewType === 'mobile'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Smartphone className="h-4 w-4" />
                <span>Mobile</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`space-y-6 ${currentViewType === 'mobile' ? 'p-4' : 'p-6'}`}>
        {/* Render each dashboard - only show if configured for current view */}
        {dashboards.filter(dashboard => {
          // Only show dashboards that are configured for the current view type
          return currentViewType === 'desktop' ? dashboard.desktopConfigured : dashboard.mobileConfigured;
        }).map((dashboard) => (
          <div key={dashboard.id} className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Dashboard Header */}
            <div className={`border-b border-gray-200 ${currentViewType === 'mobile' ? 'px-4 py-3' : 'px-6 py-4'}`}>
              <div className={`flex ${currentViewType === 'mobile' ? 'flex-col space-y-4' : 'items-center justify-between'}`}>
                <div className={currentViewType === 'mobile' ? 'w-full' : ''}>
                  <div className={`flex items-center ${currentViewType === 'mobile' ? 'flex-col space-y-2' : 'space-x-3'}`}>
                    {editingTitleId === dashboard.id ? (
                      <input
                        ref={titleInputRef}
                        type="text"
                        value={editingTitle}
                        onChange={(e) => handleTitleChange(dashboard.id, e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Escape') {
                            handleCancelTitleEdit();
                          }
                        }}
                        className="text-xl font-bold text-gray-900 bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                      />
                    ) : (
                      <div className="flex items-center space-x-2">
                        <h2 
                          className={`text-xl font-bold text-gray-900 ${
                            isEditMode ? 'cursor-pointer hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-all' : ''
                          }`}
                          onClick={() => {
                            if (isEditMode) {
                              handleStartTitleEdit(dashboard.id, dashboard.name);
                            }
                          }}
                          title={isEditMode ? "Click to edit dashboard name" : ""}
                        >
                          {dashboard.name}
                        </h2>
                      </div>
                    )}
                    {/* Current view indicator */}
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                        currentViewType === 'desktop' 
                          ? 'text-blue-700 bg-blue-100' 
                          : 'text-green-700 bg-green-100'
                      }`}>
                        {currentViewType === 'desktop' ? (
                          <>
                            <Monitor className="h-3 w-3 mr-1" />
                            Desktop
                          </>
                        ) : (
                          <>
                            <Smartphone className="h-3 w-3 mr-1" />
                            Mobile
                          </>
                        )}
                      </span>
                      {/* Show independence indicator when both views are configured */}
                      {dashboard.desktopConfigured && dashboard.mobileConfigured && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full" 
                              title="Desktop and mobile views are independent - reports added to one won't appear in the other">
                          Independent Views
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className={`flex items-center ${currentViewType === 'mobile' ? 'flex-wrap gap-2 justify-center' : 'space-x-3'}`}>
                  <DateRangeSelector
                    value={dashboard.dateRange || 'this_month'}
                    onChange={(newDateRange) => handleDateRangeChange(dashboard.id, newDateRange)}
                    defaultValue={dashboard.defaultDateRange}
                    onSetDefault={(newDefaultDateRange) => handleSetDefaultDateRange(dashboard.id, newDefaultDateRange)}
                  />
                  
                  {/* View Type Switching */}
                  {isEditMode && (
                    <>
                      <button
                        onClick={async () => {
                          if (dashboard.desktopConfigured) {
                            setCurrentViewType('desktop');
                            // Reload dashboard data for desktop view
                            await loadDashboards('desktop');
                          } else {
                            // Switch to desktop view immediately, then configure
                            setCurrentViewType('desktop');
                            handleConfigureOppositeView(dashboard.id, 'desktop');
                          }
                        }}
                        disabled={configuringView}
                        className={`${currentViewType === 'mobile' ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'} font-medium rounded-lg flex items-center space-x-1 transition-colors border disabled:opacity-50 ${
                          currentViewType === 'desktop'
                            ? 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100'
                            : !dashboard.desktopConfigured
                            ? 'text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100'
                            : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <Monitor className={currentViewType === 'mobile' ? 'h-3 w-3' : 'h-4 w-4'} />
                        <span>
                          {!dashboard.desktopConfigured 
                            ? (configuringView ? 'Configuring...' : 'Configure Desktop View')
                            : 'Desktop'
                          }
                        </span>
                      </button>
                      <button
                        onClick={async () => {
                          if (dashboard.mobileConfigured) {
                            setCurrentViewType('mobile');
                            // Reload dashboard data for mobile view
                            await loadDashboards('mobile');
                          } else {
                            // Switch to mobile view immediately, then configure
                            setCurrentViewType('mobile');
                            handleConfigureOppositeView(dashboard.id, 'mobile');
                          }
                        }}
                        disabled={configuringView}
                        className={`${currentViewType === 'mobile' ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'} font-medium rounded-lg flex items-center space-x-1 transition-colors border disabled:opacity-50 ${
                          currentViewType === 'mobile'
                            ? 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100'
                            : !dashboard.mobileConfigured
                            ? 'text-green-700 bg-green-50 border border-green-200 hover:bg-green-100'
                            : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <Smartphone className={currentViewType === 'mobile' ? 'h-3 w-3' : 'h-4 w-4'} />
                        <span>
                          {!dashboard.mobileConfigured 
                            ? (configuringView ? 'Configuring...' : 'Configure Mobile View')
                            : 'Mobile'
                          }
                        </span>
                      </button>
                    </>
                  )}
                  
                  <button
                    onClick={toggleEditMode}
                    className={`${currentViewType === 'mobile' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} font-medium rounded-lg flex items-center space-x-1 transition-colors ${
                      isEditMode 
                        ? 'text-white bg-blue-600 hover:bg-blue-700' 
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {isEditMode ? <Eye className={currentViewType === 'mobile' ? 'h-3 w-3' : 'h-4 w-4'} /> : <Edit3 className={currentViewType === 'mobile' ? 'h-3 w-3' : 'h-4 w-4'} />}
                    <span>{isEditMode ? 'View Mode' : 'Edit'}</span>
                  </button>
                  {isEditMode && (
                    <button 
                      onClick={() => handleAddReportsClick(dashboard.id)}
                      className={`${currentViewType === 'mobile' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-1`}
                      title={`Add reports to ${currentViewType} view only`}
                    >
                    <FileBarChart className={currentViewType === 'mobile' ? 'h-3 w-3' : 'h-4 w-4'} />
                    <span>Add Reports</span>
                  </button>
                  )}
                  {isEditMode && (
                  <button
                    onClick={() => handleDeleteDashboard(dashboard.id)}
                    className="text-gray-400 hover:text-red-600 p-2 rounded-lg hover:bg-gray-50"
                  >
                      <Trash2 className="h-5 w-5" />
                  </button>
                  )}
                </div>
              </div>
            </div>

            {/* Dashboard Canvas */}
            <div className={currentViewType === 'mobile' ? 'p-4' : 'p-6'}>
              <div
                className={`min-h-[500px] relative overflow-hidden rounded-xl bg-white ${
                  isEditMode 
                    ? 'border-2 border-dashed border-gray-300' 
                    : 'border border-gray-200'
                }`}
                style={isEditMode ? {
                  backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                } : {}}
              >
                {/* Dashboard content rendering logic */}
                {(() => {
                  // Check if current view is configured
                  const isCurrentViewConfigured = currentViewType === 'desktop' 
                    ? dashboard.desktopConfigured 
                    : dashboard.mobileConfigured;
                  
                  // If current view is not configured, show empty state
                  if (!isCurrentViewConfigured) {
                    return (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                        <div className="mb-4">
                          {currentViewType === 'desktop' ? (
                            <Monitor className="h-16 w-16 mx-auto text-gray-300" />
                          ) : (
                            <Smartphone className="h-16 w-16 mx-auto text-gray-300" />
                          )}
                        </div>
                        <h3 className="text-lg font-medium mb-2">
                          {currentViewType === 'desktop' ? 'Desktop' : 'Mobile'} view not configured
                        </h3>
                        <p className="text-sm text-gray-400 mb-4">
                          This dashboard doesn't have a {currentViewType} layout configured yet.
                        </p>
                      </div>
                    );
                  }

                  return dashboard.reports && dashboard.reports.length > 0 ? (
                    /* Show Reports with Drag & Drop */
                    <div className={`${currentViewType === 'mobile' ? 'p-2' : 'p-4'}`} style={{ minHeight: '600px' }}>
                      {/* Edit mode precision resizing indicator */}
                      {isEditMode && (
                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                              </svg>
                            </div>
                            <div className="text-sm text-blue-800">
                              <strong>Smart Canvas:</strong> {currentViewType === 'desktop' ? '100×30' : '20×40'} default grid, auto-expands with content • 
                              Drag to move • Hover for resize handles • {currentViewType === 'desktop' ? '8px' : '5px'} row height for dot-by-dot control
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Smart canvas with responsive grid */}
                      <div 
                        style={{
                          minHeight: `${getGridHeight(dashboard)}px` // Smart canvas: Desktop 100x30 (240px), Mobile 20x40 (200px), expands with content
                        }}
                        className="w-full"
                      >
                        <GridLayout
                        className="layout"
                        data-edit-mode={isEditMode}
                        layout={dashboard.reports.map(report => ({
                          i: report.id,
                          x: report.layout.x,
                          y: report.layout.y,
                          w: report.layout.w,
                          h: report.layout.h
                        }))}
                        cols={currentViewType === 'mobile' ? 20 : 100}  // Fixed grid: Mobile = 20 cols, Desktop = 100 cols
                        rowHeight={currentViewType === 'mobile' ? 5 : 8}  // Ultra-fine vertical control: 5px mobile, 8px desktop
                        onLayoutChange={(layout: any[]) => handleLayoutChange(layout, dashboard.id)}
                        isDraggable={isEditMode}
                        isResizable={isEditMode}
                        margin={[2, 2]}  // Smaller margins for finer positioning
                        containerPadding={[4, 4]}  // Reduced padding for more grid space
                        resizeHandles={['se', 'sw', 'ne', 'nw', 's', 'n', 'e', 'w']}  // All resize handles for maximum control
                        autoSize={true} // Let GridLayout auto-size vertically based on content
                        verticalCompact={false}
                        compactType={null}
                        width={currentViewType === 'mobile' ? 400 : 1200} // Fixed width for consistent scaling
                      >
                      {dashboard.reports.map((report) => (
                        <div key={report.id} className={`bg-white border border-gray-200 rounded-lg ${
                          getResponsivePadding(report.layout)
                        } shadow-sm h-full flex flex-col transition-all relative overflow-hidden ${
                          isEditMode 
                            ? 'hover:shadow-md hover:border-blue-300 cursor-move' 
                            : 'hover:shadow-sm'
                        }`}>
                          {isEditMode && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteReport(dashboard.id, report.id);
                              }}
                              className="absolute top-2 right-2 text-gray-400 hover:text-red-600 p-1 rounded-full hover:bg-gray-100 z-10"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                          
                          {/* Title Element Rendering */}
                          {report.type === 'title' ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-center">
                              {isEditMode ? (
                                <input
                                  type="text"
                                  value={reportTitleTexts[report.id] ?? report.data?.text ?? 'Dashboard Title'}
                                  onChange={(e) => handleReportTitleChange(dashboard.id, report.id, e.target.value)}
                                  className={`${getTitleFontSize(report.layout).class} font-bold text-gray-900 bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center w-full`}
                                />
                              ) : (
                                <div 
                                  className={`${getTitleFontSize(report.layout).class} font-bold text-gray-900 transition-all duration-200 text-center w-full px-2 overflow-hidden`}
                                >
                                  <div className="line-clamp-2">
                                    {report.data?.text || 'Dashboard Title'}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : report.type === 'stock_alert' ? (
                            /* Stock Alert Custom Layout */
                            <div className="flex flex-col">
                              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                                <div className="flex items-center space-x-3">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                    report.data?.status === 'healthy' ? 'bg-green-100' : 'bg-red-100'
                                  }`}>
                                    {report.data?.status === 'healthy' ? (
                                      <CheckCircle className="h-4 w-4 text-green-600" />
                                    ) : (
                                      <AlertTriangle className="h-4 w-4 text-red-600" />
                                    )}
                                  </div>
                                  <h4 className="text-sm font-semibold text-gray-900">
                                    {report.data?.status === 'healthy' ? 'Healthy Stock' : 'Low Stock'}
                                  </h4>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <div className="text-lg font-bold text-gray-900">{report.data?.value}</div>
                                  <div className="text-xs text-gray-500">{report.data?.label}</div>
                                </div>
                              </div>
                              {/* Supplier Orders List */}
                              <div className="flex-1 mt-2">
                                {supplierOrders.length === 0 ? (
                                  <div className="text-center py-4">
                                    <Package2 className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                                    <p className="text-xs text-gray-500">No suppliers need orders</p>
                                  </div>
                                ) : (
                                  <div className="max-h-40 overflow-y-auto space-y-2">
                                    {supplierOrders.map((order) => (
                                      <div 
                                        key={order.supplier.id} 
                                        className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex-1 min-w-0">
                                            <h5 className="text-xs font-medium text-gray-900 truncate">
                                              {order.supplier.first_name} {order.supplier.last_name}
                                            </h5>
                                            <p className="text-xs text-gray-500">
                                              {order.itemCount} item{order.itemCount !== 1 ? 's' : ''} • ${order.totalCost.toFixed(2)}
                                            </p>
                                          </div>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handlePlaceOrder(order.supplier.id);
                                            }}
                                            className="ml-2 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
                                          >
                                            <ShoppingCart className="h-3 w-3 inline-block mr-1" />
                                            Order
                                          </button>
                                        </div>
                                        <div className="space-y-1">
                                          {order.items.slice(0, 2).map((item) => (
                                            <div key={item.item.id} className="flex justify-between text-xs text-gray-600">
                                              <span className="truncate">{item.item.name}</span>
                                              <span>{item.quantityToOrder} {item.item.unit}</span>
                                            </div>
                                          ))}
                                          {order.items.length > 2 && (
                                            <div className="text-xs text-gray-500">
                                              +{order.items.length - 2} more item{order.items.length - 2 !== 1 ? 's' : ''}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'online_booking_volume' ? (
                            /* Online Booking Volume Funnel Custom Layout */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                                <div className="flex items-center space-x-2">
                                  <div className="w-6 h-6 rounded-lg bg-purple-100 flex items-center justify-center">
                                    <Monitor className="h-3 w-3 text-purple-600" />
                                  </div>
                                  <h4 className="text-xs font-semibold text-gray-900 truncate">
                                    {report.title}
                                  </h4>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm font-bold text-gray-900">
                                    {report.data?.value || '0.0%'}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {report.data?.label || 'Conversion Rate'}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Funnel Steps */}
                              <div className="flex-1 min-h-0 overflow-hidden">
                                <div className="space-y-1">
                                  {(report.data?.funnelData || []).map((step: any, index: number) => {
                                    const maxCount = Math.max(...(report.data?.funnelData || []).map((s: any) => s.count));
                                    const percentage = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
                                    
                                    return (
                                      <div key={step.step} className="flex items-center space-x-2">
                                        <div className="flex-1 bg-gray-100 rounded-sm h-3 relative overflow-hidden">
                                          <div 
                                            className={`h-full rounded-sm transition-all duration-300 ${
                                              index === 0 ? 'bg-blue-500' :
                                              index === 1 ? 'bg-green-500' :
                                              index === 2 ? 'bg-yellow-500' :
                                              index === 3 ? 'bg-orange-500' :
                                              index === 4 ? 'bg-red-500' :
                                              'bg-purple-500'
                                            }`}
                                            style={{ width: `${percentage}%` }}
                                          />
                                          <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-xs font-medium text-gray-700">
                                              {step.count}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="text-xs text-gray-600 min-w-0 flex-shrink-0 w-16 truncate">
                                          {step.step}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* Regular Data Report Rendering */
                            <>
                              <div className={`flex items-center ${
                                getResponsiveSpacing(report.layout).replace('space-y-', 'mb-').split(' ')[0]
                              } mb-2 flex-shrink-0`}>
                                <div className={`${
                                  getIconSize(report.layout).class
                                } rounded-lg flex items-center justify-center ${
                                  report.type === 'upsells' 
                                    ? 'bg-blue-100' 
                                    : report.type === 'closed_sales'
                                    ? 'bg-purple-100'
                                    : report.type === 'supplies_purchased'
                                    ? 'bg-orange-100'
                                    : report.type === 'net_profit'
                                    ? 'bg-emerald-100'
                                    : report.type === 'labour'
                                    ? 'bg-amber-100'
                                    : report.type === 'job_materials'
                                    ? 'bg-teal-100'
                                    : report.type === 'total_jobs'
                                    ? 'bg-indigo-100'
                                    : report.type === 'completed_jobs'
                                    ? 'bg-cyan-100'
                                    : report.type === 'online_booking_volume'
                                    ? 'bg-purple-100'
                                    : 'bg-green-100'
                                }`}>
                                  {report.type === 'total_jobs' ? (
                                    <FileBarChart className={`${
                                      getIconSize(report.layout).class
                                    } text-indigo-600`} />
                                  ) : report.type === 'completed_jobs' ? (
                                    <CheckCircle className={`${
                                      getIconSize(report.layout).class
                                    } text-cyan-600`} />
                                  ) : report.type === 'online_booking_volume' ? (
                                    <Monitor className={`${
                                      getIconSize(report.layout).class
                                    } text-purple-600`} />
                                  ) : (
                                    <DollarSign className={`${
                                      getIconSize(report.layout).class
                                    } ${
                                      report.type === 'upsells'
                                        ? 'text-blue-600'
                                        : report.type === 'closed_sales'
                                        ? 'text-purple-600'
                                        : report.type === 'supplies_purchased'
                                        ? 'text-orange-600'
                                        : report.type === 'net_profit'
                                        ? 'text-emerald-600'
                                        : report.type === 'labour'
                                        ? 'text-amber-600'
                                        : report.type === 'job_materials'
                                        ? 'text-teal-600'
                                        : report.type === 'online_booking_volume'
                                        ? 'text-purple-600'
                                        : 'text-green-600'
                                    }`} />
                                  )}
                                </div>
                                <h4 className={`${
                                  getTitleFontSize(report.layout).class
                                } font-semibold text-gray-900 truncate`}>
                                  {report.title}
                                </h4>
                              </div>
                              <div className="flex-1 flex flex-col items-center justify-center text-center min-h-0">
                                <div className={`${getContentFontSize(report.layout, String(report.data?.value || '').length).class} font-bold text-gray-900 transition-all duration-200 truncate w-full px-1`}>
                                  {report.data?.value}
                                </div>
                                <div className={`text-xs text-gray-500 mt-1 text-center leading-tight px-1 overflow-hidden`}>
                                  <div className="line-clamp-2">
                                    {report.data?.label}
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                        </GridLayout>
                      </div>
                    </div>
                    ) : (
                      /* Empty state for individual dashboard with smart canvas height */
                      <div 
                        className="flex flex-col items-center justify-center" 
                        style={{ minHeight: `${getGridHeight(dashboard)}px` }}
                      >
                        <Activity className="h-12 w-12 mb-4 text-gray-300" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {isEditMode ? 'Start Building Your Dashboard' : 'No Reports Added Yet'}
                        </h3>
                        <p className="text-center max-w-md text-gray-500 mb-6">
                          {isEditMode 
                            ? 'Click "Add Reports" to add widgets and reports to your dashboard.'
                            : 'Switch to Edit mode to add reports and customize this dashboard.'
                          }
                        </p>
                      </div>
                    );
                })()}
              </div>
            </div>
          </div>
        ))}

        {/* Create Dashboard Button */}
        <div className="flex flex-col items-center">
          {dashboards.filter(dashboard => {
            return currentViewType === 'desktop' ? dashboard.desktopConfigured : dashboard.mobileConfigured;
          }).length === 0 ? (
            // Initial state for current view type
            <>
              <div className="flex flex-col items-center justify-center min-h-[400px]">
                {/* Check if there are dashboards in the opposite view that can be configured */}
                {dashboards.some(dashboard => 
                  currentViewType === 'desktop' ? dashboard.mobileConfigured && !dashboard.desktopConfigured 
                  : dashboard.desktopConfigured && !dashboard.mobileConfigured
                ) ? (
                  <div className="text-center max-w-md">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      No {currentViewType} dashboards configured
                    </h3>
                    <p className="text-gray-600 text-sm mb-6">
                      You have dashboards configured for {currentViewType === 'desktop' ? 'mobile' : 'desktop'} view. 
                      Configure them for {currentViewType} view or create a new independent dashboard.
                    </p>
                    <div className="space-y-3">
                      <button 
                        onClick={() => setIsModalOpen(true)}
                        className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4 inline mr-2" />
                        Create New {currentViewType === 'desktop' ? 'Desktop' : 'Mobile'} Dashboard
                      </button>
                      <p className="text-xs text-gray-500">
                        Desktop and mobile dashboards are completely independent - reports added to one won't appear in the other
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-gray-600 text-sm mb-4">Create Your First {currentViewType === 'desktop' ? 'Desktop' : 'Mobile'} Dashboard</p>
                    <button 
                      onClick={() => setIsModalOpen(true)}
                      className="bg-white rounded-full shadow-lg border border-gray-200 p-4 hover:shadow-xl transition-shadow"
                    >
                      <Plus className="h-8 w-8 text-gray-400" />
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            // After dashboards exist - smaller button below
            <button 
              onClick={() => setIsModalOpen(true)}
              className="px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 flex items-center space-x-2 shadow-sm"
            >
              <Plus className="h-4 w-4" />
              <span>Add Another Dashboard</span>
            </button>
          )}
        </div>
      </div>

      {/* Create Dashboard Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Create New {currentViewType === 'desktop' ? 'Desktop' : 'Mobile'} Dashboard
              </h2>
              <button
                onClick={handleModalClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <label htmlFor="dashboard-name" className="block text-sm font-medium text-gray-700 mb-2">
                Dashboard Name
              </label>
              <input
                id="dashboard-name"
                type="text"
                value={dashboardName}
                onChange={(e) => setDashboardName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateDashboard()}
                placeholder="Enter dashboard name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-2">
                This dashboard will be created for {currentViewType} view only. You can configure the opposite view later if needed.
              </p>
            </div>
            
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={handleModalClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateDashboard}
                disabled={!dashboardName.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Reports Modal */}
      {isAddReportsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Add Reports to {currentViewType === 'desktop' ? 'Desktop' : 'Mobile'} View
              </h2>
              <button
                onClick={handleAddReportsModalClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Available Reports</h3>
              <p className="text-xs text-gray-500 mb-4">
                Reports added here will only appear in the {currentViewType} view. 
                Each view is independent after configuration.
              </p>
              
              {/* Design Elements Section */}
              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-3">Design Elements</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Title Element */}
                  <div 
                    onClick={() => handleReportClick('title', 'Title')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                        <Type className="h-5 w-5 text-gray-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Title</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">Dashboard Title</div>
                      <div className="text-xs text-gray-500">Add custom text</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Basic Sales Section */}
              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-3">Basic Sales</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Sales Report */}
                  <div 
                    onClick={() => handleReportClick('sales', 'Sales')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                        <DollarSign className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Sales</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$24,567</div>
                      <div className="text-xs text-gray-500">Total Sales After Discounts</div>
                    </div>
                  </div>

                  {/* Upsells Report */}
                  <div 
                    onClick={() => handleReportClick('upsells', 'Upsells')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                        <DollarSign className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Upsells</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$8,245</div>
                      <div className="text-xs text-gray-500">Total Upsells</div>
                    </div>
                  </div>

                  {/* Closed Sales Report */}
                  <div 
                    onClick={() => handleReportClick('closed_sales', 'Closed Sales')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                        <DollarSign className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Closed Sales</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$18,765</div>
                      <div className="text-xs text-gray-500">Completed Jobs</div>
                    </div>
                  </div>

                  {/* Supplies Purchased Report */}
                  <div 
                    onClick={() => handleReportClick('supplies_purchased', 'Supplies Purchased')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                        <DollarSign className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Supplies Purchased</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$5,230</div>
                      <div className="text-xs text-gray-500">Total Supplies Cost</div>
                    </div>
                  </div>

                  {/* Net Profit Report */}
                  <div 
                    onClick={() => handleReportClick('net_profit', 'Net Profit')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                        <DollarSign className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Net Profit</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$12,450</div>
                      <div className="text-xs text-gray-500">Bottom Line Profit</div>
                    </div>
                  </div>

                  {/* Labour Report */}
                  <div 
                    onClick={() => handleReportClick('labour', 'Labour')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                        <DollarSign className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Labour</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$6,789</div>
                      <div className="text-xs text-gray-500">Total Labour Costs</div>
                    </div>
                  </div>

                  {/* Job Materials Report */}
                  <div 
                    onClick={() => handleReportClick('job_materials', 'Job Materials')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center group-hover:bg-teal-200 transition-colors">
                        <DollarSign className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Job Materials</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$4,321</div>
                      <div className="text-xs text-gray-500">Total Job Materials</div>
                    </div>
                  </div>

                  {/* Total Jobs Report */}
                  <div 
                    onClick={() => handleReportClick('total_jobs', 'Total Jobs')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                        <FileBarChart className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Total Jobs</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">47</div>
                      <div className="text-xs text-gray-500">Total Jobs Booked</div>
                    </div>
                  </div>

                  {/* Completed Jobs Report */}
                  <div 
                    onClick={() => handleReportClick('completed_jobs', 'Completed Jobs')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center group-hover:bg-cyan-200 transition-colors">
                        <CheckCircle className="h-5 w-5 text-cyan-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Completed Jobs</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">32</div>
                      <div className="text-xs text-gray-500">Completed Jobs</div>
                    </div>
                  </div>

                  {/* Stock Alert Report */}
                  <div 
                    onClick={() => handleReportClick('stock_alert', 'Stock Alert')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Stock Alert</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">Live Status</div>
                      <div className="text-xs text-gray-500">Current Stock Health</div>
                    </div>
                  </div>

                  {/* Online Booking Volume Report */}
                  <div 
                    onClick={() => handleReportClick('online_booking_volume', 'Online Booking Volume')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                        <Monitor className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Online Booking Volume</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">25.0%</div>
                      <div className="text-xs text-gray-500">Conversion Funnel</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end">
              <button
                onClick={handleAddReportsModalClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Wrap with PermissionGuard
function KPIDashboardWithPermission() {
  return (
    <PermissionGuard permission="kpiDashboard.pageAccess">
      <KPIDashboard />
    </PermissionGuard>
  );
}

export default KPIDashboardWithPermission; 